import { action, computed, makeObservable, observable } from 'mobx'

/**
 * A generic base store for multistep flows. TStep should be an enum
 * describing the available steps for the store that extends this.
 * Pass the ordered steps array to super() in the subclass constructor.
 */
export abstract class MultiStepStore<TStep extends string> {
    protected constructor(steps: TStep[]) {
        this._steps = steps
        makeObservable<MultiStepStore<TStep>, '_currentIndex'>(this, {
            _currentIndex: observable,
            currentStep: computed,
            isFirstStep: computed,
            isLastStep: computed,
            nextStep: action,
            prevStep: action,
            setStep: action,
            resetStep: action,
        })
    }

    private readonly _steps: TStep[]

    private _currentIndex = 0

    get currentStep(): TStep {
        return this._steps[this._currentIndex]
    }

    get isFirstStep(): boolean {
        return this._currentIndex === 0
    }

    get isLastStep(): boolean {
        return this._currentIndex === this._steps.length - 1
    }

    nextStep(): void {
        if (!this.isLastStep) {
            this._currentIndex++
        }
    }

    prevStep(): void {
        if (!this.isFirstStep) {
            this._currentIndex--
        }
    }

    setStep(step: TStep): void {
        const index = this._steps.indexOf(step)
        if (index !== -1) {
            this._currentIndex = index
        }
    }

    resetStep(): void {
        this._currentIndex = 0
    }
}
